import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useU2000AuthContext } from '../../../u2000Core/context/auth/U2000UseAuthContext';
import { useToast } from '../../../u2000Core/context/toast/U2000ToastContext';
import { NS } from '../../U3204';
import type { U3204MessageSource } from '../../U3204Service';
import { u3204HasMessageSources } from '../../utils/u3204HasMessageSources';
import { u3204MessageSourceLinkLabel } from '../../utils/u3204MessageSourceLinkLabel';
import { u3204OpenOrgCitationSource } from '../../utils/u3204OpenOrgCitationSource';

interface U3204MessageSourcesProps {
  sources: U3204MessageSource[] | null | undefined;
}

function u3204MessageSourceListKey(source: U3204MessageSource, index: number): string {
  if (source.chunkId) {
    return source.chunkId;
  }
  if (source.kind === 'org') {
    return `${source.sourceId}-${source.sectionPath}-${index}`;
  }
  return `${source.logicalUnitCode}-${source.sectionPath}-${index}`;
}

const U3204MessageSources: React.FC<U3204MessageSourcesProps> = ({ sources }) => {
  const { t } = useTranslation(NS);
  const { auth, tokenRef } = useU2000AuthContext();
  const toast = useToast();
  const [openingKey, setOpeningKey] = useState<string | null>(null);

  const handleOrgClick = async (
    source: Extract<U3204MessageSource, { kind: 'org' }>,
    listKey: string,
  ) => {
    if (openingKey) {
      return;
    }
    setOpeningKey(listKey);
    try {
      // Préférer tokenRef (toujours à jour) puis auth.token — même pattern Axios
      const token = tokenRef.current ?? auth.token;
      await u3204OpenOrgCitationSource(source.url, token);
    } catch {
      toast.error(t('chat.sourcesOrgOpenError', { ns: NS }));
    } finally {
      setOpeningKey(null);
    }
  };

  if (!u3204HasMessageSources(sources)) {
    return null;
  }

  return (
    <div className="mt-1.5 w-full text-xs text-muted-foreground" data-testid="u3204-message-sources">
      <p className="mb-1 font-medium text-neutral-600">{t('chat.sources', { ns: NS })}</p>
      <ul className="space-y-0.5">
        {sources.map((source, index) => {
          const listKey = u3204MessageSourceListKey(source, index);
          const label = u3204MessageSourceLinkLabel(source);

          if (source.kind === 'org') {
            const isOpening = openingKey === listKey;
            return (
              <li key={listKey}>
                <button
                  type="button"
                  data-testid="u3204-message-source-org"
                  disabled={Boolean(openingKey)}
                  onClick={() => void handleOrgClick(source, listKey)}
                  className="text-left text-primary-600 underline-offset-2 hover:underline disabled:cursor-wait disabled:opacity-60"
                >
                  {isOpening ? `${label}…` : label}
                </button>
              </li>
            );
          }

          return (
            <li key={listKey}>
              <a
                href={source.url}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="u3204-message-source-product"
                className="text-primary-600 underline-offset-2 hover:underline"
              >
                {label}
              </a>
            </li>
          );
        })}
      </ul>
    </div>
  );
};

export default U3204MessageSources;
